// S3 · "how close is my catalogue to go-live" (Module 02 §4 S3, §3.6).
//
// The four `catalogue.*` go-live checks, read from the same checklist the S8
// screen and the go-live gate read. Deliberately a READ plus an explicit
// re-check button: `GET /v1/onboarding/checklist` never evaluates (§5), so a
// never-evaluated checklist must show its items as "not checked yet" with a CTA
// — never an empty panel and never an implied all-clear.

import { enumLabel } from "../../../lib/enum-label";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { Button } from "../../../components/button";
import { CardSkeleton, ErrorState } from "../../../components/states";
import { formatTimestamp } from "../../../lib/datetime";
import { requestIdOf } from "../../../lib/request";
import { useChecklist, useEvaluateChecklist } from "../hooks/onboarding-queries";
import { useOnboardingPermissions } from "../hooks/permissions";
import { useTerm } from "../hooks/terminology";
import { WizardProblemNotice } from "../components/wizard-notices";

/** The catalogue slice of the closed §3.6 catalog, in catalog order. */
export const CATALOGUE_CHECKLIST_ITEMS = [
  "catalogue.products_min",
  "catalogue.boms_cover_products",
  "catalogue.routings_cover_products",
  "catalogue.routings_bound",
] as const;

/** Item keys contain dots, which i18next reads as path separators — so the
 * dictionary keys use underscores and this is the one place that converts. */
export function catalogueItemLabelKey(itemKey: string): string {
  return `onboarding.catalogueStep.items.${itemKey.replace(/\./g, "_")}`;
}

export function CatalogueProgress({ frozen }: { frozen: boolean }) {
  const { t, i18n } = useTranslation();
  const { term } = useTerm();
  const perms = useOnboardingPermissions();
  const checklist = useChecklist();
  const evaluate = useEvaluateChecklist();

  const terms = {
    product: term("product"),
    bom: term("bom"),
    workCenter: term("work_center"),
  };

  if (checklist.isPending) {
    return <CardSkeleton lines={4} label={t("onboarding.catalogueStep.progress.loading")} />;
  }

  if (checklist.error !== null) {
    return (
      <ErrorState
        title={t("onboarding.catalogueStep.progress.errorTitle")}
        detail={t("onboarding.state.errorDetail")}
        retryLabel={t("onboarding.state.retry")}
        onRetry={() => void checklist.refetch()}
        requestId={requestIdOf(checklist.error)}
        requestIdLabel={t("onboarding.state.requestId")}
      />
    );
  }

  const items = checklist.data.items;
  const stored = new Map(items.map((item) => [item.item_key, item]));
  // §5: `GET /checklist` is a pure read. `items: []` means "never evaluated",
  // which must show as four pending checks with a CTA — never an implied pass.
  const neverEvaluated = items.length === 0;
  // §7: re-evaluation is a wizard mutation (onboarding.manage_wizard), and every
  // wizard mutation answers 409 ALREADY_LIVE once the tenant is live.
  const canEvaluate = perms.has("onboarding.manage_wizard") && !frozen;

  return (
    <section className="catalogue-progress ds-card" data-testid="catalogue-progress">
      <header className="import-section-head">
        <h3>{t("onboarding.catalogueStep.progress.title")}</h3>
        <p className="wizard-step-blurb">{t("onboarding.catalogueStep.progress.blurb")}</p>
      </header>

      {evaluate.error !== null ? (
        <WizardProblemNotice
          error={evaluate.error}
          onRetry={() => {
            evaluate.reset();
          }}
        />
      ) : null}

      {neverEvaluated ? (
        <p className="wizard-step-blurb" data-testid="checklist-never-evaluated">
          {t("onboarding.catalogueStep.progress.neverEvaluated")}
        </p>
      ) : null}

      <ul className="catalogue-progress-list">
        {CATALOGUE_CHECKLIST_ITEMS.map((key) => {
          const item = stored.get(key);
          const status = item?.status ?? "pending";
          return (
            <li key={key} className="catalogue-progress-item" data-status={status} data-item={key}>
              <span className="catalogue-progress-title">
                {t(catalogueItemLabelKey(key), { defaultValue: key, ...terms })}
              </span>
              <span className={`badge status-${status}`}>
                {enumLabel(t, "onboarding.checklist.itemStatus", status)}
              </span>
              {item?.blocking === true ? (
                <span className="badge badge-blocking">
                  {t("onboarding.catalogueStep.progress.blocking")}
                </span>
              ) : null}
              <span className="catalogue-progress-when">
                {item?.evaluated_at != null
                  ? t("onboarding.catalogueStep.progress.lastEvaluated", {
                      time: formatTimestamp(item.evaluated_at, i18n.language),
                    })
                  : t("onboarding.catalogueStep.progress.never")}
              </span>
            </li>
          );
        })}
      </ul>

      <div className="import-actions">
        {canEvaluate ? (
          <Button
            busy={evaluate.isPending}
            busyLabel={t("onboarding.catalogueStep.progress.evaluating")}
            onClick={() => {
              evaluate.mutate();
            }}
          >
            {t("onboarding.catalogueStep.progress.evaluate")}
          </Button>
        ) : null}
        <Link to="/onboarding/checklist" className="wizard-header-link">
          {t("onboarding.catalogueStep.progress.openChecklist")}
        </Link>
      </div>
    </section>
  );
}
